import type { InteriorWoodSpecies } from "../finishes/types";
import { resolveBenchSlats } from "./benchSlats";
import { resolveBenchBackrest } from "./benchBackrest";
/**
 * Bench BOM, derived from the layout.
 *
 * This replaces `lmLavar = ceil((W − 0.2) × 14)` — a width heuristic that
 * multiplied the building's OUTER width by a magic 14 and never once looked
 * at the benches the model draws. A 2.0 m long sauna and a 4.0 m long one
 * bought the same bench timber; changing the bench geometry changed nothing
 * in the price.
 *
 * Every quantity below is traced to a mesh `addSlattedBench` actually emits:
 *
 * | Part                | Mesh family              | Lumber |
 * |---------------------|--------------------------|--------|
 * | Slats               | `detail/bench-slat/*`    | 28×90  |
 * | Wall ledger         | `detail/bench-ledger/*`  | 45×90  |
 * | Front board         | `detail/bench-rail/*-front` | 45×70 |
 * | End rails + joists  | `detail/bench-rail/*`    | 45×70  |
 * | Front legs          | `detail/bench-leg/*`     | 45×70  |
 * | Foot-lav skirt      | `detail/bench-skirt/*`   | 28×90  |
 *
 * Fasteners are the one row with no mesh: screws are a consumable, counted
 * from the joints the frame actually has and classified as such.
 */
import type { CutRow, MaterialRow } from "../../engines/types";
import {
	BENCH_FRAME_HEIGHT_MM,
	BENCH_FRAME_THICKNESS_MM,
	BENCH_LEDGER_HEIGHT_MM,
	BENCH_SCREWS_PER_PACK,
	BENCH_SLAT_THICKNESS_MM,
	BENCH_SLAT_WIDTH_MM,
	benchJoistCount,
} from "./constants";
import { resolveBenchSkirt } from "./benchSkirt";
import { resolveBenchSupport } from "./benchSupport";
import { benchFrameDimensions } from "./benchFrame";
import { resolveEffectiveLayout } from "./effectiveRuns";
import type {
	BenchRun,
	HeaterSizeMm,
	InteriorLayout,
	InteriorRoom,
} from "./types";

const SCREWS_PER_FRAME_METRE = 6;
const SCREWS_PER_SLAT_END = 2;

/** 45×70 — the section every rail, joist and leg is cut from. */
const FRAME_DIMENSION_LABEL = `${BENCH_FRAME_THICKNESS_MM}×${BENCH_FRAME_HEIGHT_MM} mm`;
const FRAME_PURCHASE_DIMENSION = `${BENCH_FRAME_THICKNESS_MM}x${BENCH_FRAME_HEIGHT_MM}`;

export interface BenchRunQuantities {
	runId: string;
	/** Slat boards, 28×90. */
	slatCount: number;
	slatLpm: number;
	/**
	 * Front rail length, also used for the back member. Wall-backed runs use
	 * a 45×90 ledger; detached runs use a 45×70 back rail in frameLpm.
	 */
	railLpm: number;
	/** Back-wall ledger or two short end-wall cleats, 45×90. */
	ledgerLpm: number;
	ledgerCount: number;
	/** End rails + cross joists, 45×70. */
	frameLpm: number;
	/** Floor posts, front and (for detached runs) back, 45×70. */
	legLpm: number;
	legCount: number;
	joistCount: number;
	/** Slatted foot-lav front and open-end boards, 28×90. */
	skirtLpm: number;
	backrestLpm: number;
}

export interface BenchMaterialsResult {
	perRun: BenchRunQuantities[];
	/** Seat slats, 28×90 kvistfritt, running metres. */
	slatLpm: number;
	/**
	 * Foot-lav skirt boards, the same 28×90 kvistfritt. Bought on the same
	 * Lav-virke row as the seat slats: `slatLpm + skirtLpm` is that row.
	 */
	skirtLpm: number;
	backrestLpm: number;
	/** Front board (45×70), running metres across all runs. */
	railLpm: number;
	/** All wall bearings, including short end-wall cleats, 45×90. */
	ledgerLpm: number;
	/** 45×70 frame, joists and legs, running metres. */
	frameLpm: number;
	fastenerCount: number;
	materials: MaterialRow[];
	cuts: CutRow[];
}

export function quantitiesForRun(
	run: BenchRun,
	room?: InteriorRoom,
): BenchRunQuantities {
	const slatCount = resolveBenchSlats(run).count;
	const joistCount = benchJoistCount(run.lengthMm);
	const {
		legHeightMm: legHeight,
		legCount,
		support,
	} = resolveBenchSupport(run, room);
	const backWallSupported = support === "wall" || support === "wall+frontLegs";
	const frame = benchFrameDimensions(
		run.lengthMm,
		run.depthMm,
		support === "endWalls",
	);
	const railLengthMm = frame.railLengthMm;

	return {
		runId: run.id,
		slatCount,
		slatLpm: (slatCount * frame.seatLengthMm) / 1000,
		railLpm: railLengthMm / 1000,
		ledgerLpm:
			support === "endWalls"
				? (2 * run.depthMm) / 1000
				: backWallSupported
					? railLengthMm / 1000
					: 0,
		ledgerCount: support === "endWalls" ? 2 : support === "floor" ? 0 : 1,
		// End-rail cuts depend on the bearing layout; joists fit between long rails.
		frameLpm:
			(2 * frame.endRailLengthMm +
				joistCount * frame.joistLengthMm +
				(backWallSupported ? 0 : railLengthMm)) /
			1000,
		legLpm: (legCount * legHeight) / 1000,
		legCount,
		joistCount,
		skirtLpm: resolveBenchSkirt(run, room)?.lpm ?? 0,
		backrestLpm: room ? resolveBenchBackrest(run, room)?.lpm ?? 0 : 0,
	};
}

export interface DeriveBenchMaterialsInput {
	layout: InteriorLayout;
	room: InteriorRoom;
	/** Omit for a room with no heater; runs are then never clipped. */
	heaterSize?: HeaterSizeMm | null;
	/**
	 * Appended to every quantity string, e.g. `" (Bastudel)"` for the two-room
	 * engine, which bills the hot room's lavar separately from the relax side.
	 * Also distinguishes the cut rows: both rooms emit a "Lavbrädor nedre", and
	 * without it a builder cannot tell which bench a length belongs to.
	 */
	quantitySuffix?: string;
	defaultWoodSpecies?: InteriorWoodSpecies;
}

export function deriveBenchMaterials(
	input: DeriveBenchMaterialsInput,
): BenchMaterialsResult {
	const { runs } = resolveEffectiveLayout(
		input.layout,
		input.room,
		input.heaterSize,
	);
	const perRun = runs.map((entry) =>
		quantitiesForRun(entry.effective, input.room),
	);

	let slatLpm = 0;
	let skirtLpm = 0;
	let backrestLpm = 0;
	let railLpm = 0;
	let ledgerLpm = 0;
	let frameLpm = 0;
	let slatEnds = 0;
	for (const q of perRun) {
		slatLpm += q.slatLpm;
		skirtLpm += q.skirtLpm;
		backrestLpm += q.backrestLpm;
		railLpm += q.railLpm;
		ledgerLpm += q.ledgerLpm;
		frameLpm += q.frameLpm + q.legLpm;
		slatEnds += q.slatCount * 2;
	}
	// Detached back rails are already included in frameLpm. Skirt boards are
	// screwed to the posts at every crossing, counted per metre like the frame.
	const fastenerCount = Math.ceil(
		(railLpm + ledgerLpm + frameLpm + skirtLpm + backrestLpm) * SCREWS_PER_FRAME_METRE +
			slatEnds * SCREWS_PER_SLAT_END,
	);
	const fastenerPacks = Math.max(
		1,
		Math.ceil(fastenerCount / BENCH_SCREWS_PER_PACK),
	);

	const suffix = input.quantitySuffix ?? "";
	const materials: MaterialRow[] = [];
	const cuts: CutRow[] = [];

	// A layout with no runs buys nothing — an empty bench list must not leave a
	// row of zeroes in the purchase plan.
	if (perRun.length > 0) {
		materials.push(
			{
				material: "Lav-virke (Kvistfritt)",
				dimension: `${BENCH_SLAT_THICKNESS_MM}×${BENCH_SLAT_WIDTH_MM} mm`,
				quantity: `${Math.ceil(slatLpm + skirtLpm + backrestLpm)} lpm${suffix}`,
				phase: "interior",
				priceRuleId: "prices.lavvirke",
				materialKey: "interior.bench-timber",
			},
			{
				material: "Lavbärlina (upplag mot vägg)",
				dimension: `${BENCH_FRAME_THICKNESS_MM}×${BENCH_LEDGER_HEIGHT_MM} mm`,
				quantity: `${Math.ceil(ledgerLpm)} lpm${suffix}`,
				phase: "interior",
				// Hidden structural rail, priced as the construction lumber it is.
				// NOT `prices.barlina`, which is the 45×145 NTR-A foundation bearer —
				// pressure-treated timber has no business inside a sauna, and it costs
				// more than twice as much.
				priceRuleId: "prices.vaggreglar",
				materialKey: "interior.bench-ledger",
			},
			{
				// The "frontpanel" of the issue: the room-facing board that closes the
				// bench front. It is a real mesh (`detail/bench-rail/*-front`), which
				// is why it gets its own row instead of an unbuilt cladding line.
				material: "Lavfront (frontbräda)",
				dimension: FRAME_DIMENSION_LABEL,
				quantity: `${Math.ceil(railLpm)} lpm${suffix}`,
				phase: "interior",
				// Visible inside the hot room, so it is bought at bench-timber grade,
				// unlike the ledger and frame behind it.
				priceRuleId: "prices.lavvirke",
				materialKey: "interior.bench-front",
			},
			{
				material: "Lavstomme (reglar & ben)",
				dimension: FRAME_DIMENSION_LABEL,
				quantity: `${Math.ceil(frameLpm)} lpm${suffix}`,
				phase: "interior",
				priceRuleId: "prices.vaggreglar",
				materialKey: "interior.bench-frame",
			},
			{
				// No mesh — a consumable. Classified by `materialKey`, so the label is
				// free to start with "Lav" without claiming to be drawn timber.
				material: "Lavskruv (ram & sittbrädor)",
				dimension: "4,5×60 / 4,0×40 mm",
				// Packages, not pieces: `prices.rostfriSkruv` is kr/paket.
				quantity: `${fastenerPacks} paket${suffix}`,
				phase: "interior",
				priceRuleId: "prices.rostfriSkruv",
				materialKey: "interior.bench-fasteners",
			},
		);
	}

	// Keep authored and room-following quantities separate before rounding and repricing.
	const mixedWood = input.layout.benchRuns.some(
		(run) => run.woodSpecies !== undefined,
	);
	if (mixedWood) {
		const groups = new Map<
			InteriorWoodSpecies | "auto",
			{ timber: number; front: number }
		>();
		runs.forEach(({ effective: run }, i) => {
			const species = run.woodSpecies ?? "auto";
			const q = perRun[i];
			const group = groups.get(species) ?? { timber: 0, front: 0 };
			group.timber += q.slatLpm + q.skirtLpm + q.backrestLpm;
			group.front += q.railLpm;
			groups.set(species, group);
		});
		for (const key of ["interior.bench-timber", "interior.bench-front"]) {
			const template = materials.find((row) => row.materialKey === key);
			if (!template) continue;
			materials.splice(
				materials.indexOf(template),
				1,
				...[...groups].map(([woodSpecies, q]) => ({
					...template,
					...(woodSpecies === "auto" ? {} : { woodSpecies }),
					quantity: `${Math.ceil(key === "interior.bench-timber" ? q.timber : q.front)} lpm${suffix}`,
				})),
			);
		}
	}
	// Cut rows carry the tier as a trailing word, not the wall: `translatePart`
	// maps the leading label and cleans the remainder, and "övre"/"nedre" are the
	// only two words that vocabulary needs. Runs sharing a tier stay
	// distinguishable by their cut length.
	//
	// `perRun` is `runs.map(...)`, so the two arrays are index-aligned.
	runs.forEach((entry, index) => {
		const q = perRun[index];
		const run = entry.effective;
		const firstCut = cuts.length;
		const resolvedSupport = resolveBenchSupport(run, input.room);
		const endSupported = resolvedSupport.support === "endWalls";
		const frame = benchFrameDimensions(run.lengthMm, run.depthMm, endSupported);
		const backWallSupported = q.ledgerCount === 1;
		const tier = `${run.tier === "upper" ? "övre" : "nedre"}${suffix}`;
		cuts.push({
			part: `Lavbrädor ${tier}${run.slatGapMm === undefined ? "" : ` (28×${resolveBenchSlats(run).widthMm.toFixed(1)} mm)`}`,
			pieces: [{ lengthMm: frame.seatLengthMm, count: q.slatCount }],
			count: `${q.slatCount} st`,
			cutLength: `${frame.seatLengthMm.toFixed(0)} mm`,
			materialKey: "interior.bench-timber",
			purchaseDimension: `${BENCH_SLAT_THICKNESS_MM}x${BENCH_SLAT_WIDTH_MM}`,
		});
		cuts.push({
			part: `${backWallSupported ? "Lavbärlina" : "Lavstomme bakregel"} ${tier}`,
			pieces: [{ lengthMm: frame.railLengthMm, count: 1 }],
			count: "1 st",
			cutLength: `${(q.railLpm * 1000).toFixed(0)} mm`,
			materialKey: backWallSupported
				? "interior.bench-ledger"
				: "interior.bench-frame",
			purchaseDimension: `${BENCH_FRAME_THICKNESS_MM}x${backWallSupported ? BENCH_LEDGER_HEIGHT_MM : BENCH_FRAME_HEIGHT_MM}`,
		});
		if (endSupported) {
			cuts.push({
				part: `Lavbärlina ${tier}`,
				pieces: [{ lengthMm: run.depthMm, count: 2 }],
				count: "2 st",
				cutLength: `${run.depthMm.toFixed(0)} mm`,
				materialKey: "interior.bench-ledger",
				purchaseDimension: `${BENCH_FRAME_THICKNESS_MM}x${BENCH_LEDGER_HEIGHT_MM}`,
			});
		}
		cuts.push({
			// The BOM buys a front board per run and the renderer draws it; without
			// its own row the cut list cannot build the bench it just sold.
			part: `Lavfront ${tier}`,
			pieces: [{ lengthMm: frame.railLengthMm, count: 1 }],
			count: "1 st",
			cutLength: `${(q.railLpm * 1000).toFixed(0)} mm`,
			materialKey: "interior.bench-front",
			purchaseDimension: FRAME_PURCHASE_DIMENSION,
		});
		// Keep separate rows: only end-supported frames put both the end rails
		// and cross joists between the full-length front and back rails.
		cuts.push({
			part: `Lavstomme gavlar ${tier}`,
			pieces: [{ lengthMm: frame.endRailLengthMm, count: 2 }],
			count: "2 st",
			cutLength: `${frame.endRailLengthMm.toFixed(0)} mm`,
			materialKey: "interior.bench-frame",
			purchaseDimension: FRAME_PURCHASE_DIMENSION,
		});
		cuts.push({
			part: `Lavstomme tvärbalkar ${tier}`,
			pieces: [{ lengthMm: frame.joistLengthMm, count: q.joistCount }],
			count: `${q.joistCount} st`,
			cutLength: `${frame.joistLengthMm.toFixed(0)} mm`,
			materialKey: "interior.bench-frame",
			purchaseDimension: FRAME_PURCHASE_DIMENSION,
		});
		const skirt = resolveBenchSkirt(run, input.room);
		if (skirt) {
			const endCount = Number(skirt.openAtStart) + Number(skirt.openAtEnd);
			const pieces = [
				{ lengthMm: skirt.frontLengthMm, count: skirt.boardCount },
				...(endCount > 0
					? [
							{
								lengthMm: skirt.endLengthMm,
								count: endCount * skirt.boardCount,
							},
						]
					: []),
			];
			cuts.push({
				part: `Lavfront spjälor ${tier}`,
				pieces,
				count: `${pieces.reduce((sum, piece) => sum + piece.count, 0)} st`,
				cutLength: pieces
					.map((piece) => `${piece.lengthMm.toFixed(0)} mm`)
					.join(" / "),
				materialKey: "interior.bench-timber",
				purchaseDimension: `${BENCH_SLAT_THICKNESS_MM}x${BENCH_SLAT_WIDTH_MM}`,
			});
		}
		const backrest = resolveBenchBackrest(run, input.room);
		if (backrest) {
			for (const [part, lengthMm, count] of [["Ryggstöd lav", backrest.lengthMm, backrest.boards.length], ["Ryggstöd distanser", backrest.heightMm, backrest.spacers.length]] as const) {
				cuts.push({ part: `${part} ${tier}`, pieces: [{ lengthMm, count }], count: `${count} st`, cutLength: `${lengthMm.toFixed(0)} mm`, materialKey: "interior.bench-timber", purchaseDimension: "28x90" });
			}
		}
		if (q.legCount > 0) {
			cuts.push({
				part: `Lavben ${tier}`,
				pieces: [{ lengthMm: resolvedSupport.legHeightMm, count: q.legCount }],
				count: `${q.legCount} st`,
				cutLength: `${resolvedSupport.legHeightMm.toFixed(0)} mm`,
				materialKey: "interior.bench-frame",
				purchaseDimension: FRAME_PURCHASE_DIMENSION,
			});
		}
		if (mixedWood) {
		 for (const cut of cuts.slice(firstCut)) {
		  if (cut.materialKey === "interior.bench-timber" || cut.materialKey === "interior.bench-front")
		   cut.woodSpecies = run.woodSpecies ?? input.defaultWoodSpecies ?? "asp";
		 }
		}
	});

	return {
		perRun,
		slatLpm,
		skirtLpm,
		backrestLpm,
		railLpm,
		ledgerLpm,
		frameLpm,
		fastenerCount,
		materials: materials.filter(
			(row) => row.materialKey !== "interior.bench-ledger" || ledgerLpm > 0,
		),
		cuts,
	};
}
